import React, { useState, useContext, useEffect } from "react";
import ActionsContext from "common/ActionsContext";
import Modal from "common/Modal.jsx";
import styled from "styled-components";

const HelpModal = () => {
  const [open, setOpen] = useState(false);
  const actions = useContext(ActionsContext);
  useEffect(() => {
    return actions.recv("open-help", function () {
      setOpen(true);
    });
  }, []);

  const doClose = () => setOpen(false);
  if (open !== true) {
    return null;
  }
  return (
    <Modal
      title="DASHBOARD"
      onClose={doClose}
      content={() => (
        <>
          <p>
            Dashboard summarizes current system status. Colored icons show
            system alarm status. Colors indicate alarm levels:
          </p>
          <ul>
            <li>
              <b>
                <span class="col-grey">Grey</span>
              </b>
              : Unknown status.
            </li>
            <li>
              <b>
                <span class="col-green">Green</span>
              </b>
              : OK. No alarm.
            </li>
            <li>
              <b>
                <span class="col-yellow">Yellow</span>
              </b>
              : Notice.
            </li>
            <li>
              <b>
                <span class="col-orange">Orange</span>
              </b>
              : Warning.
            </li>
            <li>
              <b>
                <span class="col-red">Red</span>
              </b>
              : Critical, the issue requires immediate attention (refer to the
              Troubleshooting section of BQN User Manual).
            </li>
          </ul>
          <h5>ALARMS PANEL</h5>
          <ul>
            <li>
              <b>CPU</b>: Raised when CPU usage increases. Red means that too
              many CPU cores are close to full. Click to go to
              Status-&gt;System-&gt;CPU.
            </li>
            <li>
              <b>DISK</b>: Raised when disk usage increases. Red means that the
              disk is close to full, not accessible or in read-only mode. Click
              to go to Status-&gt;System-&gt;Disk.
            </li>
            <li>
              <b>NETWORK INTERFACES</b>: Status of the user traffic network
              interfaces (wires). Click to go to
              Status-&gt;Interfaces-&gt;Data_Wires.
              <ul>
                <li>
                  Green if all wires are available (all interfaces participating
                  in wires are up, with their links also up).
                </li>
                <li>Yellow if at least one wire is available.</li>
                <li>Orange if wires being configured.</li>
                <li>Red if no wires are configured.</li>
              </ul>
            </li>
            <li>
              <b>MEMORY</b>: Raised when memory usage increases. A tooltip
              message indicates the status of the memory pools (DPDK or the main
              memory pool). Click to go to Status-&gt;System-&gt;Memory.
            </li>
            <li>
              <b>LOW TRAFFIC</b>: Green when there is apparently normal traffic
              flowing through in both directions. Yellow if the traffic is too
              low. Orange if there is no traffic in the uplink or downlink
              directions. A tooltip message will show which of the 4 anomalous
              situations, if any, has been detected: low traffic, no uplink
              traffic, no downlink traffic. Click to go to
              Statistics-&gt;Throughput-&gt;Interfaces.
            </li>
            <li>
              <b>INVERTED TRAFFIC</b>: Orange if the uplink traffic exceeds the
              downlink traffic (a sign that one or more wires might be inverted,
              i.e. with the access side connected to the Internet). Click to go
              to Statistics-&gt;Throughput-&gt;Interfaces.
            </li>
            <li>
              <b>NTP</b>: Yellow if no NTP servers are configured, orange if
              none of the configured NTP servers are synchronized. Click to go
              to Administration-&gt;System_Date-&gt;NTP_Servers.
            </li>
            <li>
              <b>LICENSE</b>: Licenses status. Red if license does not exists or
              is invalid (a tooltip message will indicate if the license is not
              valid/available, or whether it has just expired). Yellow if there
              is no connection with the license manager. Orange if the is
              aproaching or exceeding license limit. A tooltip message will show
              which of the 4 anomalous situations, if any, has been detected:
              license-available, license-expiration, license-usage or
              license-mgr-conn. Click to go to Administration- &gt;License.
            </li>
            <li>
              <b>BILLING SYSTEM SYNC</b>: Raised when a billing system is
              configured to provide subscriber information and the communication
              with the billing system fails. Click to go to Status-&gt;Billing.
            </li>
          </ul>
          <h5>SYSTEM INFORMATION</h5>
          <ul>
            <li>
              <b>System Name</b>: BQN server host name.{" "}
            </li>
            <li>
              <b>BQN Release</b>: BQN software package release. Click to go to
              Administration-&gt;Software.
            </li>
            <li>
              <b>Hardware ID</b>: Internal hardware identifier.{" "}
            </li>
            <li>
              <b>Start Time</b>: Time when the server was last started
              (year-month-day hour:minutes:seconds +offset from UTC).{" "}
            </li>
            <li>
              <b>Serial Number</b>: Hardware serial number (n/a if unknown).{" "}
            </li>
            <li>
              <b>System Time</b>: System current date and time, with same format
              as Start time.{" "}
            </li>
            <li>
              <b>License Limit</b>: License throughput capacity. Click to go to
              Administration-&gt;License.
            </li>
            <li>
              <b>License Expiration</b>: License expiration date, with same
              format as Start time. Click to go to Administration-&gt;License.
            </li>
          </ul>
          <h5>TRAFFIC INFORMATION</h5>
          <p>For Downlink and Uplink directions:</p>
          <ul>
            <li>
              Total transmitted throughput in that direction. Click to got to
              Statistics-&gt;Throughput-&gt;Overview.
            </li>
            <li>
              Packets per Seconds (PPS). Click to got to
              Statistics-&gt;Throughput-&gt;Overview.
            </li>
            <li>
              Percentage of throughput subject to shaping (by subscriber flow
              policies). Click to got to
              Statistics-&gt;Throughput-&gt;Policies(flow).
            </li>
            <li>
              Percentage of throughput subject to rate limits (by subscriber
              rate policies). Click to got to
              Statistics-&gt;Throughput-&gt;Policies(rate).
            </li>
            <li>
              Downlink latency: round-trip times on access side (downlink
              direction). Click to got to Statistics-&gt;System-&gt;Latency.
            </li>
            <li>
              Uplink latency: round-trip times on Internet side (uplink
              direction). Click to got to Statistics-&gt;DPI-&gt;Latency.
              Please, bear in mind that this number may differ from the one in
              that page, because that number is calculated for connections for
              which we have DPI records, and this number is for all TCP
              connections.
            </li>
            <li>
              Percentage of TCP flows with TCP optimization. Click to got to
              Status-&gt;TCPO-&gt;Speed&Acceleration.
            </li>
            <li>
              Active subscribers (one access IP address counted as one
              subscriber). Click to got to
              Status-&gt;Subscribers-&gt;QoE_Metrics.
            </li>
            <li>
              Subscriber groups defined. Click to got to
              Status-&gt;Subscribers-&gt;Groups.
            </li>
            <li>
              Active flows (TCP connections, UDP flows and other IP protocol
              flows). Click to got to Status-&gt;Flows-&gt;Details.
            </li>
          </ul>
          <p>
            Click on the <i class="material-icons valign-icon">refresh</i> icon
            or wait for 10 seconds to refresh status information.
          </p>
        </>
      )}
    />
  );
};
export default HelpModal;
